import { describeEditableField, formatTimestamp } from '@fit-editor/fit-core'
import type {
  EditableFieldDescription,
  EditFieldValue,
  TimeDisplayMode,
} from '@fit-editor/fit-core'

import type { FieldKind } from '@/lib/fit-data'
import { toSnakeCase } from '@/lib/fit-data'
import { dateToInputValue, inputValueToDate } from '@/lib/time-input'

/**
 * Pure helpers behind in-place field editing in the FIT data view (the
 * `editField` operation). All validation rules come from fit-core's
 * `describeEditableField`; this module only adds the checks that depend
 * on what the *cell* shows (unknown message types, developer/undocumented
 * fields, array values) and turns form input into the value `editField`
 * expects -- always in the FIT field's own units, never the app's km/mi
 * display units.
 */

/** What a cell knows about the field it shows (a subset of `FieldCellContext`). */
export interface FieldEditTarget {
  messagesKey: string | undefined
  fieldId: string
  kind: FieldKind
  value: unknown
}

const NOT_EDITABLE = (reason: string): EditableFieldDescription => ({
  editable: false,
  kind: 'number',
  reason,
})

const descriptionCache = new Map<string, EditableFieldDescription>()

/** `describeEditableField`, memoized: it depends only on (messagesKey, field), and tables call it per cell. */
function describeCached(messagesKey: string, field: string): EditableFieldDescription {
  const key = `${messagesKey}\u0000${field}`
  let description = descriptionCache.get(key)
  if (description == null) {
    description = describeEditableField(messagesKey, field)
    descriptionCache.set(key, description)
  }
  return description
}

/** Whether (and how) the field shown in a cell can be edited with `editField`. */
export function describeFieldEdit(target: FieldEditTarget): EditableFieldDescription {
  if (target.messagesKey == null) {
    return NOT_EDITABLE(
      "this message type isn't defined by the FIT Profile; it is preserved exactly as recorded and is not editable",
    )
  }
  if (target.kind === 'developer') {
    return NOT_EDITABLE(
      'developer fields are preserved exactly as recorded and are not editable in this version',
    )
  }
  if (target.kind === 'undocumented') {
    return NOT_EDITABLE(
      "this field isn't defined by the FIT Profile; it is preserved exactly as recorded and is not editable",
    )
  }
  if (Array.isArray(target.value)) {
    return NOT_EDITABLE('array values are not editable in this version')
  }
  return describeCached(target.messagesKey, target.fieldId)
}

/** "lap 2" for (`lapMesgs`, 1). */
export function messageInstanceLabel(messagesKey: string, index: number): string {
  return `${toSnakeCase(messagesKey.replace(/Mesgs$/, ''))} ${index + 1}`
}

/** `enumChoice` value meaning "enter a raw integer instead of a named value". */
export const RAW_ENUM_CHOICE = '__raw__'

/** The editor's form state. `text` backs every text/number/date input; `enumChoice` the enum select. */
export interface FieldEditDraft {
  text: string
  enumChoice: string
}

/** A number without float noise (`0.1 + 0.2` -> `0.3`), for prefilling an input. */
function plainNumber(value: number): string {
  return String(Number(value.toPrecision(15)))
}

/** The form state prefilled from the field's current value (empty when the field isn't set). */
export function initialDraft(
  description: EditableFieldDescription,
  value: unknown,
  timeMode: TimeDisplayMode,
): FieldEditDraft {
  switch (description.kind) {
    case 'enum':
      if (typeof value === 'string' && description.enumValues?.includes(value) === true) {
        return { text: '', enumChoice: value }
      }
      if (typeof value === 'number')
        return { text: plainNumber(value), enumChoice: RAW_ENUM_CHOICE }
      return { text: '', enumChoice: '' }
    case 'date':
      return {
        text: value instanceof Date ? dateToInputValue(value, timeMode) : '',
        enumChoice: '',
      }
    case 'string':
      return { text: typeof value === 'string' ? value : '', enumChoice: '' }
    case 'number':
      if (typeof value === 'boolean') return { text: value ? '1' : '0', enumChoice: '' }
      return { text: typeof value === 'number' ? plainNumber(value) : '', enumChoice: '' }
  }
}

export function utf8ByteLength(text: string): number {
  return new TextEncoder().encode(text).length
}

/** A bound for messages: plain number, with units if any. */
function formatBound(value: number, units: string | undefined): string {
  return `${plainNumber(value)}${units != null ? ` ${units}` : ''}`
}

export type ParseFieldResult = { ok: true; value: EditFieldValue } | { ok: false; error: string }

const fail = (error: string): ParseFieldResult => ({ ok: false, error })

/**
 * Validates the form against the field's description and returns the
 * value to send in an `editField` op. Mirrors fit-core's own checks so
 * the user sees the problem inline before anything is applied; fit-core
 * still validates again (and has the final say, e.g. on instance-specific
 * rules such as an inactive sub-field).
 */
export function parseFieldDraft(
  description: EditableFieldDescription,
  draft: FieldEditDraft,
  timeMode: TimeDisplayMode,
): ParseFieldResult {
  const { min, max, units } = description
  switch (description.kind) {
    case 'number': {
      const text = draft.text.trim()
      if (text === '') return fail('Enter a value.')
      const n = Number(text)
      if (!Number.isFinite(n)) return fail('Enter a valid number.')
      if (description.integer === true && !Number.isInteger(n)) {
        return fail('Enter a whole number.')
      }
      if ((min != null && n < min) || (max != null && n > max)) {
        return fail(
          `Enter a value from ${formatBound(min ?? -Infinity, units)} to ${formatBound(max ?? Infinity, units)}.`,
        )
      }
      return { ok: true, value: n }
    }
    case 'enum': {
      if (draft.enumChoice === '') return fail('Choose a value.')
      if (draft.enumChoice !== RAW_ENUM_CHOICE) {
        if (description.enumValues?.includes(draft.enumChoice) !== true) {
          return fail(`"${draft.enumChoice}" is not a valid value for this field.`)
        }
        return { ok: true, value: draft.enumChoice }
      }
      const text = draft.text.trim()
      if (text === '') return fail('Enter a raw value.')
      const n = Number(text)
      if (!Number.isInteger(n) || (min != null && n < min) || (max != null && n > max)) {
        return fail(`Enter a whole number from ${min ?? 0} to ${max ?? 0}.`)
      }
      return { ok: true, value: n }
    }
    case 'string': {
      const text = draft.text
      if (text === '') return fail('Enter some text, or use "Remove field" instead.')
      if (text.includes('\0')) return fail('Text cannot contain a NUL character.')
      const bytes = utf8ByteLength(text)
      if (description.maxBytes != null && bytes > description.maxBytes) {
        return fail(
          `The text is ${bytes} bytes (UTF-8); the maximum is ${description.maxBytes} bytes.`,
        )
      }
      return { ok: true, value: text }
    }
    case 'date': {
      if (draft.text.trim() === '') return fail('Enter a date and time.')
      const date = inputValueToDate(draft.text, timeMode)
      if (date == null) return fail('Enter a valid date and time.')
      if ((min != null && date.getTime() < min) || (max != null && date.getTime() > max)) {
        return fail(
          `Enter a date between ${formatTimestamp(new Date(min ?? 0), timeMode)} and ${formatTimestamp(new Date(max ?? 0), timeMode)}.`,
        )
      }
      // FIT dates are whole seconds (the input's step is 1 s; be safe anyway).
      return { ok: true, value: new Date(Math.floor(date.getTime() / 1000) * 1000) }
    }
  }
}

/**
 * fit-core's `editField` errors read "editField lapMesgs #2 totalDistance:
 * <reason>." -- the prefix repeats what the editor already shows, so it is
 * dropped for inline display.
 */
export function stripEditFieldErrorPrefix(message: string): string {
  const stripped = message.replace(/^editField \S+ #\d+ \S+: /, '')
  return stripped.charAt(0).toUpperCase() + stripped.slice(1)
}
